import {ShortcutEnum} from '@wallaby-ui/document';

/** The filter form of the hospitalizations list, and the stored shape of its saved filters (component hospitalizationList). */
export type PatientsFilters = {
  patientFullName?: string;
  patientIdentificationNumber?: string;
  patientSrcId?: string;
  lastEncounterSubOrganizationIds?: string[];
  encounterOrganizationIds?: string[];
  lastEncounterSubStartedFrom?: string;
  lastEncounterSubStartedTo?: string;
  lastEncounterSubFloatingShortcutName?: ShortcutEnum | null;
  encounterStartedFrom?: string;
  encounterStartedTo?: string;
  encounterFloatingShortcutName?: ShortcutEnum | null;
  hospitalizationStates?: string[];
  riskScoreFrom?: number;
  riskScoreTo?: number;
  riskFactorTypes?: string[];
  riskFactorsCountFrom?: number;
  riskFactorsCountTo?: number;
  commentSearchString?: string;
  // lastMedicationsCountFrom?: string;
  // lastMedicationsCountTo?: string;
  atcHierarchyIds1?: string[];
  atcHierarchyIds2?: string[];
  atcHierarchyIds3?: string[];
  activeIngredientIds1?: string[];
  activeIngredientIds2?: string[];
  activeIngredientIds3?: string[];
  atcHierarchyFrom?: string;
  atcHierarchyTo?: string;
  atcHierarchyFloatingShortcutName?: ShortcutEnum | null;
  atcHierarchyMatch?: string;
  biochemistryMethod1?: string;
  biochemistryOperator1?: string;
  biochemistryValue1?: string;
  biochemistryMethod2?: string;
  biochemistryOperator2?: string;
  biochemistryValue2?: string;
  biochemistryMethod3?: string;
  biochemistryOperator3?: string;
  biochemistryValue3?: string;
  biochemistryMethod4?: string;
  biochemistryOperator4?: string;
  biochemistryValue4?: string;
  biochemistryMethod5?: string;
  biochemistryOperator5?: string;
  biochemistryValue5?: string;
  biochemistryFrom?: string;
  biochemistryTo?: string;
  biochemistryFloatingShortcutName?: ShortcutEnum | null;
  biochemistryMatch?: string;
  pathogenCodes1?: string[];
  resistanceAtbCodes1?: string[];
  pathogenCodes2?: string[];
  resistanceAtbCodes2?: string[];
  pathogenCodes3?: string[];
  resistanceAtbCodes3?: string[];
  microbiologyFrom?: string;
  microbiologyTo?: string;
  microbiologyFloatingShortcutName?: ShortcutEnum | null;
  microbiologyMatch?: string;
  // Other tab (PDM-2749): structured ICD diagnoses, patient attributes as inclusive ranges, death options.
  icdCodes?: string[];
  ageFrom?: number;
  ageTo?: number;
  weightFrom?: number;
  weightTo?: number;
  bmiFrom?: number;
  bmiTo?: number;
  deathDuringHospitalizations?: string[];
  // filters before PhRec
  riskSeverities?: string[];
  evaluationStates?: string[];
  evaluationStateModifiedFrom?: string;
  evaluationStateModifiedTo?: string;
  // filters after PhRec
  pharmaRecommendationRiskinesses?: string[];
  pharmaRecommendationDateFrom?: string;
  pharmaRecommendationDateTo?: string;
  pharmaRecommendationFloatingShortcutName?: ShortcutEnum | null;
  pharmaRecommendationFollowUpDateFrom?: string;
  pharmaRecommendationFollowUpDateTo?: string;
  pharmaRecommendationFollowUpFloatingShortcutName?: ShortcutEnum | null;
};

export const DEFAULT_PATIENTS_FILTERS: PatientsFilters = {
  hospitalizationStates: ['ACTIVE'],
  atcHierarchyMatch: 'ANY_DAY',
  // Same default as the medication box, so its selector shows "any day" instead of an empty field.
  biochemistryMatch: 'ANY_DAY',
  microbiologyMatch: 'ANY_DAY',
};

/**
 * The occurrence selects (medication, laboratory, microbiology) always show a value. The backend reads a missing
 * occurrence as "any day", and a filter arriving from a bookmark or from a saved filter written before the key
 * existed carries none - filled on the way in, so that the field, the chips and the saved-filter comparison all see
 * the same value instead of an empty select over a list that is in fact filtered "any day".
 */
export function withOccurrenceDefaults<T extends Partial<PatientsFilters>>(patients: T): T {
  return {
    ...patients,
    atcHierarchyMatch: patients.atcHierarchyMatch ?? DEFAULT_PATIENTS_FILTERS.atcHierarchyMatch,
    biochemistryMatch: patients.biochemistryMatch ?? DEFAULT_PATIENTS_FILTERS.biochemistryMatch,
    microbiologyMatch: patients.microbiologyMatch ?? DEFAULT_PATIENTS_FILTERS.microbiologyMatch,
  };
}
